import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft, Plus, Camera, Trash2, Save, CheckCircle, Clock,
  Home, User, Calendar, FileText, Loader2, ChevronDown, ChevronRight,
  Image, Edit, X, Zap, PenTool, Download, Send, AlertTriangle,
  ZoomIn, Upload, ImagePlus, ChevronLeft, UserCheck, Link, GitCompare, Copy, Key
} from "lucide-react";
import { API } from "@/App";
import SignaturePad from "@/components/SignaturePad";

const CONDITION_OPTIONS = [
  { value: "excellent", label: "Excellent", color: "bg-green-500" },
  { value: "good", label: "Good", color: "bg-emerald-500" },
  { value: "fair", label: "Fair", color: "bg-amber-500" },
  { value: "poor", label: "Poor", color: "bg-orange-500" },
  { value: "very_poor", label: "Very Poor", color: "bg-red-500" },
  { value: "new", label: "New Item", color: "bg-blue-500" }
];

const CLEANLINESS_OPTIONS = [
  { value: "excellent", label: "Excellent", color: "bg-green-500" },
  { value: "good", label: "Good", color: "bg-emerald-500" },
  { value: "fair", label: "Fair", color: "bg-amber-500" },
  { value: "poor", label: "Poor", color: "bg-orange-500" },
  { value: "very_poor", label: "Very Poor", color: "bg-red-500" }
];

export default function ReportEditorPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [report, setReport] = useState(null);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modals
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showMeterReading, setShowMeterReading] = useState(false);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [keyDraft, setKeyDraft] = useState({ label: '', quantity: 1, notes: '' });
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  
  // Current selections
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedRooms, setExpandedRooms] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  // Signature state
  const [signatureType, setSignatureType] = useState("tenant");
  const [signerName, setSignerName] = useState("");
  const [savingSignature, setSavingSignature] = useState(false);
  
  // Form states
  const [newRoom, setNewRoom] = useState({ room_name: "", room_type: "bedroom" });
  const [newItem, setNewItem] = useState({ 
    item_name: "", 
    description: "", 
    condition: "good", 
    cleanliness: "good",
    notes: ""
  });
  const [meterReading, setMeterReading] = useState({
    meter_type: "electricity",
    reading: "",
    notes: ""
  });
  const [tenantLink, setTenantLink] = useState(null);
  const [showTenantLink, setShowTenantLink] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchReport();
    fetchTemplates();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/inventory-reports/${reportId}`, { headers });
      setReport(res.data);
      // Expand first room by default
      if (res.data.rooms?.length > 0) {
        setExpandedRooms([res.data.rooms[0].room_id]);
      }
    } catch (error) {
      toast.error("Failed to load report");
      navigate("/inventory/reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/inventory-reports/templates`, { headers });
      setTemplates(res.data);
    } catch (error) {
      console.error("Failed to load templates");
    }
  };

  const handleAddTemplateRoom = async (templateKey) => {
    try {
      await axios.post(`${API}/inventory-reports/${reportId}/add-template-room?template_key=${templateKey}`, {}, { headers });
      toast.success("Room added with items");
      fetchReport();
      setShowAddRoom(false);
    } catch (error) {
      toast.error("Failed to add room");
    }
  };

  const handleAddCustomRoom = async () => {
    if (!newRoom.room_name) {
      toast.error("Please enter a room name");
      return;
    }
    
    try {
      await axios.post(`${API}/inventory-reports/rooms`, {
        report_id: reportId,
        room_name: newRoom.room_name,
        room_type: newRoom.room_type
      }, { headers });
      toast.success("Room added");
      fetchReport();
      setShowAddRoom(false);
      setNewRoom({ room_name: "", room_type: "bedroom" });
    } catch (error) {
      toast.error("Failed to add room");
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!confirm("Delete this room and all its items?")) return;
    
    try {
      await axios.delete(`${API}/inventory-reports/rooms/${roomId}`, { headers });
      toast.success("Room deleted");
      fetchReport();
    } catch (error) {
      toast.error("Failed to delete room");
    }
  };

  const handleAddItem = async () => {
    if (!newItem.item_name || !selectedRoom) {
      toast.error("Please enter an item name");
      return;
    }
    
    try {
      await axios.post(`${API}/inventory-reports/items`, {
        room_id: selectedRoom.room_id,
        ...newItem
      }, { headers });
      toast.success("Item added");
      fetchReport();
      setShowAddItem(false);
      setNewItem({ item_name: "", description: "", condition: "good", cleanliness: "good", notes: "" });
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      await axios.patch(`${API}/inventory-reports/items/${itemId}`, updates, { headers });
      fetchReport();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm("Delete this item?")) return;
    
    try {
      await axios.delete(`${API}/inventory-reports/items/${itemId}`, { headers });
      toast.success("Item deleted");
      fetchReport();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handlePhotoUpload = async (itemId, file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      try {
        const formData = new FormData();
        formData.append("item_id", itemId);
        formData.append("photo_data", base64);
        formData.append("caption", "");
        
        await axios.post(`${API}/inventory-reports/photos`, formData, { 
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        toast.success("Photo uploaded");
        fetchReport();
      } catch (error) {
        toast.error("Failed to upload photo");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleMultiplePhotoUpload = async (itemId, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    setUploadProgress({ current: 0, total: files.length });
    
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        const formData = new FormData();
        formData.append("item_id", itemId);
        formData.append("photo_data", base64);
        formData.append("caption", "");
        
        await axios.post(`${API}/inventory-reports/photos`, formData, { 
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error("Failed to upload photo:", error);
      }
    }
    
    setUploadingPhotos(false);
    setShowPhotoUpload(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully`);
      fetchReport();
    }
    if (failCount > 0) {
      toast.error(`${failCount} photo${failCount > 1 ? 's' : ''} failed to upload`);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/inventory-reports/photos/${photoId}`, { headers });
      toast.success("Photo deleted");
      fetchReport();
      // Close lightbox if showing this photo
      if (lightboxPhoto?.photo_id === photoId) {
        setShowPhotoLightbox(false);
      }
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const openPhotoLightbox = (photos, index) => {
    setLightboxPhotos(photos);
    setLightboxIndex(index);
    setLightboxPhoto(photos[index]);
    setShowPhotoLightbox(true);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < lightboxPhotos.length) {
      setLightboxIndex(newIndex);
      setLightboxPhoto(lightboxPhotos[newIndex]);
    }
  };

  const openPhotoUploadModal = (item) => {
    setSelectedItem(item);
    setShowPhotoUpload(true);
  };

  const handleAddMeterReading = async () => {
    if (!meterReading.reading) {
      toast.error("Please enter a meter reading");
      return;
    }
    
    try {
      await axios.post(`${API}/inventory-reports/meters`, {
        report_id: reportId,
        ...meterReading
      }, { headers });
      toast.success("Meter reading added");
      fetchReport();
      setShowMeterReading(false);
      setMeterReading({ meter_type: "electricity", reading: "", notes: "" });
    } catch (error) {
      toast.error("Failed to add meter reading");
    }
  };
  
  const handleSaveKeys = async (nextKeys) => {
    try {
      await axios.put(`${API}/inventory-reports/${reportId}`, { keys: nextKeys }, { headers });
      setReport(prev => ({ ...prev, keys: nextKeys }));
      toast.success("Keys updated");
    } catch (error) {
      toast.error("Failed to save keys");
    }
  };

  const handleAddSignature = async (signatureData) => {
    if (!signerName.trim()) {
      toast.error("Please enter the signer's name");
      return;
    }
    
    setSavingSignature(true);
    try {
      await axios.post(`${API}/inventory-reports/signatures`, {
        report_id: reportId,
        signer_type: signatureType,
        signer_name: signerName.trim(),
        signature_data: signatureData
      }, { headers });
      
      toast.success(`${signatureType === 'tenant' ? 'Tenant' : 'Inspector'} signature saved`);
      fetchReport();
      setShowSignature(false);
      setSignerName("");
      setSignatureType("tenant");
      
      // If both signatures are collected, mark as complete
      const updatedReport = await axios.get(`${API}/inventory-reports/${reportId}`, { headers });
      const signatures = updatedReport.data.signatures || [];
      const hasTenant = signatures.some(s => s.signer_type === 'tenant');
      const hasInspector = signatures.some(s => s.signer_type === 'inspector');
      
      if (hasTenant && hasInspector && report.status === 'awaiting_signature') {
        await handleUpdateStatus('complete');
        toast.success("Report marked as complete!");
      }
    } catch (error) {
      console.error("Signature error:", error);
      toast.error("Failed to save signature");
    } finally {
      setSavingSignature(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axios.patch(`${API}/inventory-reports/${reportId}`, { status }, { headers });
      toast.success("Status updated");
      fetchReport();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info("Generating PDF...");
      const response = await axios.get(`${API}/inventory-reports/${reportId}/pdf`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from headers or create one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${report?.report_type_label || 'Report'} - ${report?.property_address || 'Property'}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const handleGenerateTenantLink = async () => {
    try {
      const response = await axios.post(`${API}/inventory-reports/${reportId}/generate-tenant-link`, {}, { headers });
      const link = `${window.location.origin}/inventory/tenant/${response.data.token}`;
      setTenantLink(link);
      setShowTenantLink(true);
      toast.success("Tenant link generated!");
    } catch (error) {
      console.error("Error generating link:", error);
      toast.error("Failed to generate tenant link");
    }
  };

  const copyTenantLink = () => {
    navigator.clipboard.writeText(tenantLink);
    toast.success("Link copied to clipboard!");
  };

  const toggleRoom = (roomId) => {
    setExpandedRooms(prev => 
      prev.includes(roomId) 
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!report) return null;

  const getConditionColor = (condition) => {
    return CONDITION_OPTIONS.find(o => o.value === condition)?.color || "bg-slate-500";
  };

  const getCleanlinessColor = (cleanliness) => {
    return CLEANLINESS_OPTIONS.find(o => o.value === cleanliness)?.color || "bg-slate-500";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-14 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/inventory/reports")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold">{report.report_type_label}</h1>
                  <Badge className={
                    report.status === "complete" ? "bg-green-100 text-green-700" :
                    report.status === "awaiting_signature" ? "bg-amber-100 text-amber-700" :
                    report.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                    "bg-slate-100 text-slate-700"
                  }>
                    {report.status.replace("_", " ")}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{report.property_address}, {report.property_postcode}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Comparison button for check-out reports */}
              {report.report_type === "check_out" && report.linked_report_id && (
                <Button variant="outline" onClick={() => navigate(`/inventory/report/${reportId}/comparison`)}>
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowMeterReading(true)}>
                <Zap className="w-4 h-4 mr-2" />
                Meter Reading
              </Button>
              <Button variant="outline" onClick={() => setShowKeysModal(true)} data-testid="keys-btn">
                <Key className="w-4 h-4 mr-2" />
                Keys
                {Array.isArray(report.keys) && report.keys.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center bg-slate-900 text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] px-1">
                    {report.keys.length}
                  </span>
                )}
              </Button>
              <Button variant="outline" onClick={handleDownloadPDF}>
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              {/* Tenant Link button */}
              {(report.status === "awaiting_signature" || report.status === "in_progress") && (
                <Button variant="outline" onClick={handleGenerateTenantLink}>
                  <Link className="w-4 h-4 mr-2" />
                  Tenant Link
                </Button>
              )}
              {report.status === "draft" && (
                <Button variant="outline" onClick={() => handleUpdateStatus("in_progress")}>
                  <Clock className="w-4 h-4 mr-2" />
                  Start Report
                </Button>
              )}
              {report.status === "in_progress" && (
                <Button onClick={() => handleUpdateStatus("awaiting_signature")} className="bg-amber-600 hover:bg-amber-700">
                  <PenTool className="w-4 h-4 mr-2" />
                  Ready for Signature
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Rooms & Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Add Room Button */}
            <Button onClick={() => setShowAddRoom(true)} className="w-full" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </Button>

            {/* Rooms List */}
            {report.rooms?.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No Rooms Added</h3>
                  <p className="text-muted-foreground mb-4">Start by adding rooms from templates or create custom rooms</p>
                  <Button onClick={() => setShowAddRoom(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Room
                  </Button>
                </CardContent>
              </Card>
            ) : (
              report.rooms.map((room) => (
                <Card key={room.room_id} className="bg-white overflow-hidden">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleRoom(room.room_id)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedRooms.includes(room.room_id) ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                      <div>
                        <h3 className="font-semibold">{room.room_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {room.item_count} items • {room.photo_count} photos
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRoom(room);
                          setShowAddItem(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Item
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRoom(room.room_id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Items */}
                  {expandedRooms.includes(room.room_id) && (
                    <div className="border-t">
                      {room.items?.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          No items in this room
                        </div>
                      ) : (
                        <div className="divide-y">
                          {room.items.map((item) => (
                            <div key={item.item_id} className="p-4 hover:bg-slate-50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-medium">{item.item_name}</span>
                                    <div className={`w-3 h-3 rounded-full ${getConditionColor(item.condition)}`} title={`Condition: ${item.condition}`} />
                                    <div className={`w-3 h-3 rounded-full ${getCleanlinessColor(item.cleanliness)}`} title={`Cleanliness: ${item.cleanliness}`} />
                                  </div>
                                  
                                  {/* Editable fields */}
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    <Select 
                                      value={item.condition}
                                      onValueChange={(v) => handleUpdateItem(item.item_id, { condition: v })}
                                    >
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CONDITION_OPTIONS.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select 
                                      value={item.cleanliness}
                                      onValueChange={(v) => handleUpdateItem(item.item_id, { cleanliness: v })}
                                    >
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CLEANLINESS_OPTIONS.map(opt => (
                                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  <Textarea
                                    placeholder="Add notes..."
                                    value={item.notes}
                                    onChange={(e) => handleUpdateItem(item.item_id, { notes: e.target.value })}
                                    className="text-sm min-h-[60px]"
                                  />

                                  {/* Photos */}
                                  <div className="mt-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-xs text-muted-foreground font-medium">
                                        Photos ({item.photos?.length || 0})
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 text-xs"
                                        onClick={() => openPhotoUploadModal(item)}
                                      >
                                        <ImagePlus className="w-3 h-3 mr-1" />
                                        Add Photos
                                      </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {item.photos?.map((photo, idx) => (
                                        <div key={photo.photo_id} className="relative group">
                                          <img 
                                            src={photo.photo_data} 
                                            alt="" 
                                            className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openPhotoLightbox(item.photos, idx)}
                                          />
                                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                                            <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeletePhoto(photo.photo_id);
                                            }}
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-md"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ))}
                                      <label className="w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 hover:border-purple-300 transition-colors">
                                        <Camera className="w-5 h-5 text-slate-400" />
                                        <span className="text-xs text-slate-400 mt-1">Upload</span>
                                        <input 
                                          type="file" 
                                          accept="image/*"
                                          multiple
                                          className="hidden"
                                          onChange={(e) => {
                                            if (e.target.files?.length > 0) {
                                              handleMultiplePhotoUpload(item.item_id, Array.from(e.target.files));
                                            }
                                          }}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-600 ml-2"
                                  onClick={() => handleDeleteItem(item.item_id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Report Info */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Report Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{report.report_type_label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Visit Date</span>
                  <span className="font-medium">
                    {report.visit_date ? format(new Date(report.visit_date), "dd MMM yyyy") : "Not set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tenant</span>
                  <span className="font-medium">{report.tenant_name || "Not specified"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created By</span>
                  <span className="font-medium">{report.created_by_name || "Unknown"}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rooms</span>
                    <span className="font-medium">{report.room_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items</span>
                    <span className="font-medium">{report.item_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Photos</span>
                    <span className="font-medium">{report.photo_count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Meter Readings */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  Meter Readings
                  <Button size="sm" variant="ghost" onClick={() => setShowMeterReading(true)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.meter_readings?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No readings recorded</p>
                ) : (
                  <div className="space-y-2">
                    {report.meter_readings.map((meter) => (
                      <div key={meter.meter_id} className="flex justify-between text-sm">
                        <span className="capitalize">{meter.meter_type}</span>
                        <span className="font-mono font-medium">{meter.reading}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signatures */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  Signatures
                  {(report.status === 'awaiting_signature' || report.status === 'in_progress') && (
                    <Button size="sm" variant="ghost" onClick={() => setShowSignature(true)}>
                      <PenTool className="w-4 h-4" />
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.signatures?.length === 0 ? (
                  <div className="text-center py-4">
                    <PenTool className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">No signatures yet</p>
                    {(report.status === 'awaiting_signature' || report.status === 'in_progress') && (
                      <Button size="sm" variant="outline" onClick={() => setShowSignature(true)}>
                        <UserCheck className="w-4 h-4 mr-2" />
                        Collect Signature
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {report.signatures.map((sig) => (
                      <div key={sig.signature_id} className="border rounded-lg p-3 bg-slate-50">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={sig.signer_type === 'tenant' ? 'default' : 'secondary'} className="capitalize">
                            {sig.signer_type}
                          </Badge>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                        <div className="font-medium">{sig.signer_name}</div>
                        {sig.signature_data && (
                          <img 
                            src={sig.signature_data} 
                            alt={`${sig.signer_name}'s signature`}
                            className="mt-2 max-h-16 border rounded bg-white"
                          />
                        )}
                        <div className="text-xs text-muted-foreground mt-2">
                          Signed: {format(new Date(sig.signed_at), "dd MMM yyyy 'at' HH:mm")}
                        </div>
                      </div>
                    ))}
                    
                    {/* Show button to add missing signature */}
                    {report.signatures.length < 2 && (report.status === 'awaiting_signature' || report.status === 'in_progress') && (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => setShowSignature(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add {report.signatures.some(s => s.signer_type === 'tenant') ? 'Inspector' : 'Tenant'} Signature
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Glossary */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Condition Key</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {CONDITION_OPTIONS.map(opt => (
                  <div key={opt.value} className="flex items-center gap-2 text-sm">
                    <div className={`w-3 h-3 rounded-full ${opt.color}`} />
                    <span>{opt.label}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Add Room Modal */}
      <Dialog open={showAddRoom} onOpenChange={setShowAddRoom}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Room</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Quick Add from Templates</Label>
              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                {Object.entries(templates).map(([key, template]) => (
                  <Button
                    key={key}
                    variant="outline"
                    className="justify-start h-auto py-2"
                    onClick={() => handleAddTemplateRoom(key)}
                  >
                    <Home className="w-4 h-4 mr-2" />
                    <div className="text-left">
                      <div className="font-medium">{template.name}</div>
                      <div className="text-xs text-muted-foreground">{template.items?.length} items</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2 block">Or Add Custom Room</Label>
              <div className="space-y-3">
                <Input
                  placeholder="Room name (e.g., Master Bedroom)"
                  value={newRoom.room_name}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, room_name: e.target.value }))}
                />
                <div className="flex gap-2">
                  <Button onClick={handleAddCustomRoom} className="flex-1">
                    Add Custom Room
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Item Modal */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Item to {selectedRoom?.room_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                placeholder="e.g., Door Frame, Radiator, Window"
                value={newItem.item_name}
                onChange={(e) => setNewItem(prev => ({ ...prev, item_name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condition</Label>
                <Select 
                  value={newItem.condition}
                  onValueChange={(v) => setNewItem(prev => ({ ...prev, condition: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cleanliness</Label>
                <Select 
                  value={newItem.cleanliness}
                  onValueChange={(v) => setNewItem(prev => ({ ...prev, cleanliness: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLEANLINESS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={newItem.notes}
                onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleAddItem}>Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meter Reading Modal */}
      <Dialog open={showMeterReading} onOpenChange={setShowMeterReading}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Meter Reading</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Meter Type</Label>
              <Select 
                value={meterReading.meter_type}
                onValueChange={(v) => setMeterReading(prev => ({ ...prev, meter_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="electricity">Electricity</SelectItem>
                  <SelectItem value="gas">Gas</SelectItem>
                  <SelectItem value="water">Water</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reading *</Label>
              <Input
                placeholder="e.g., 12345"
                value={meterReading.reading}
                onChange={(e) => setMeterReading(prev => ({ ...prev, reading: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                placeholder="Any notes about the meter"
                value={meterReading.notes}
                onChange={(e) => setMeterReading(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMeterReading(false)}>Cancel</Button>
            <Button onClick={handleAddMeterReading}>Add Reading</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Keys Modal — handed-over/returned keys per report */}
      <Dialog open={showKeysModal} onOpenChange={setShowKeysModal}>
        <DialogContent className="max-w-lg" data-testid="keys-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Keys & Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {Array.isArray(report.keys) && report.keys.length > 0 ? (
              <div className="space-y-2" data-testid="keys-list">
                {report.keys.map((k, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{k.label || 'Unnamed key'}</p>
                      <p className="text-xs text-slate-500">
                        {k.quantity > 1 ? `${k.quantity} sets · ` : ''}{k.notes || ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={async () => {
                        const next = report.keys.filter((_, i) => i !== idx);
                        await handleSaveKeys(next);
                      }}
                      data-testid={`key-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No keys recorded yet.</p>
            )}
            
            <div className="border-t pt-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add a key set</p>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Input
                  placeholder="Label (e.g. Front Door, Window Lock)"
                  value={keyDraft.label}
                  onChange={(e) => setKeyDraft({ ...keyDraft, label: e.target.value })}
                  data-testid="key-label-input"
                />
                <Input
                  type="number"
                  min="1"
                  placeholder="Qty"
                  value={keyDraft.quantity}
                  onChange={(e) => setKeyDraft({ ...keyDraft, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  className="w-20"
                  data-testid="key-qty-input"
                />
              </div>
              <Input
                placeholder="Notes (optional, e.g. on Yale fob, returned 12/05)"
                value={keyDraft.notes}
                onChange={(e) => setKeyDraft({ ...keyDraft, notes: e.target.value })}
                data-testid="key-notes-input"
              />
              <Button
                onClick={async () => {
                  if (!keyDraft.label.trim()) {
                    toast.error('Add a key label');
                    return;
                  }
                  const next = [...(report.keys || []), { ...keyDraft, label: keyDraft.label.trim() }];
                  await handleSaveKeys(next);
                  setKeyDraft({ label: '', quantity: 1, notes: '' });
                }}
                className="w-full"
                data-testid="key-add-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Key
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowKeysModal(false)} data-testid="keys-modal-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox Modal */}
      <Dialog open={showPhotoLightbox} onOpenChange={setShowPhotoLightbox}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
          <div className="relative">
            {/* Close button */}
            <button 
              onClick={() => setShowPhotoLightbox(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation buttons */}
            {lightboxPhotos.length > 1 && (
              <>
                <button 
                  onClick={() => navigateLightbox(-1)}
                  disabled={lightboxIndex === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 disabled:opacity-30 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button 
                  onClick={() => navigateLightbox(1)}
                  disabled={lightboxIndex === lightboxPhotos.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 disabled:opacity-30 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Main image */}
            <div className="flex items-center justify-center min-h-[500px] p-8">
              {lightboxPhoto && (
                <img 
                  src={lightboxPhoto.photo_data} 
                  alt="" 
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              )}
            </div>

            {/* Photo info & actions */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              <div className="flex items-center justify-between text-white">
                <div>
                  <p className="text-sm opacity-70">
                    Photo {lightboxIndex + 1} of {lightboxPhotos.length}
                  </p>
                  {lightboxPhoto?.caption && (
                    <p className="mt-1">{lightboxPhoto.caption}</p>
                  )}
                  {lightboxPhoto?.captured_at && (
                    <p className="text-xs opacity-50 mt-1">
                      Captured: {format(new Date(lightboxPhoto.captured_at), "dd MMM yyyy 'at' HH:mm")}
                    </p>
                  )}
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => handleDeletePhoto(lightboxPhoto?.photo_id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Photo
                </Button>
              </div>
            </div>

            {/* Thumbnail strip */}
            {lightboxPhotos.length > 1 && (
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg">
                {lightboxPhotos.map((photo, idx) => (
                  <button
                    key={photo.photo_id}
                    onClick={() => {
                      setLightboxIndex(idx);
                      setLightboxPhoto(photo);
                    }}
                    className={`w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                      idx === lightboxIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={photo.photo_data} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Modal */}
      <Dialog open={showPhotoUpload} onOpenChange={setShowPhotoUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Photos</DialogTitle>
            <DialogDescription>
              Add photos for: {selectedItem?.item_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {uploadingPhotos ? (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 animate-spin mx-auto text-purple-600 mb-4" />
                <p className="text-lg font-medium">Uploading Photos...</p>
                <p className="text-muted-foreground">
                  {uploadProgress.current} of {uploadProgress.total}
                </p>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-4">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <label className="block">
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors">
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium mb-1">Drop photos here</p>
                  <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                  <p className="text-xs text-muted-foreground">
                    Supports: JPG, PNG, HEIC • Multiple files allowed
                  </p>
                </div>
                <input 
                  type="file" 
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length > 0 && selectedItem) {
                      handleMultiplePhotoUpload(selectedItem.item_id, Array.from(e.target.files));
                    }
                  }}
                />
              </label>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoUpload(false)} disabled={uploadingPhotos}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Collect Signature
            </DialogTitle>
            <DialogDescription>
              Draw signature below using mouse or touch. This will be added to the inventory report.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Signer Type Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Signer Type</Label>
                <Select 
                  value={signatureType}
                  onValueChange={(v) => setSignatureType(v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tenant">Tenant</SelectItem>
                    <SelectItem value="inspector">Inspector</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Full Name</Label>
                <Input 
                  placeholder="Enter signer's full name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Declaration */}
            <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
              <p className="font-medium mb-2">Declaration:</p>
              <p>
                I confirm that I have reviewed this inventory report and agree that it accurately 
                represents the condition of the property at the time of inspection. I understand 
                that this signature is legally binding.
              </p>
            </div>

            {/* Signature Pad */}
            {savingSignature ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mr-3" />
                <span>Saving signature...</span>
              </div>
            ) : (
              <SignaturePad 
                onSave={handleAddSignature}
                onClear={() => {}}
                width={500}
                height={200}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSignature(false);
              setSignerName("");
            }} disabled={savingSignature}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Link Modal */}
      <Dialog open={showTenantLink} onOpenChange={setShowTenantLink}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5" />
              Tenant Access Link
            </DialogTitle>
            <DialogDescription>
              Share this link with the tenant so they can review and sign the report.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <Label className="text-xs text-muted-foreground mb-2 block">Shareable Link</Label>
              <div className="flex items-center gap-2">
                <Input 
                  readOnly 
                  value={tenantLink || ""} 
                  className="bg-white font-mono text-sm"
                />
                <Button onClick={copyTenantLink} size="icon" variant="outline">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This link allows the tenant to:
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• View the full inventory report</li>
                <li>• See all room conditions and photos</li>
                <li>• Add their signature electronically</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTenantLink(false)}>
              Close
            </Button>
            <Button onClick={copyTenantLink}>
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
